import { useEffect } from "react";

const SITE = "https://www.noordb.co.uk";

function upsertMeta(attr, key, content) {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

export default function Seo({ title, description, path = "/", image, imageWidth = 1200, imageHeight = 630, schema, noindex = false }) {
  useEffect(() => {
    if (title) document.title = title;
    upsertMeta("name", "description", description);
    // The build-time prerender serves the site locally (127.0.0.1/localhost) — snapshots
    // must carry PRODUCTION robots meta, so the local snapshot host is treated as production.
    const host = window.location.hostname;
    const isSnapshotHost = host === "127.0.0.1" || host === "localhost";
    const isPreview = !isSnapshotHost && host !== "www.noordb.co.uk" && host !== "noordb.co.uk";
    upsertMeta("name", "robots", noindex ? "noindex, follow" : (isPreview ? "noindex, nofollow" : "index, follow, max-image-preview:large"));
    upsertMeta("property", "og:title", title);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:url", `${SITE}${path}`);
    upsertMeta("property", "og:type", "website");
    upsertMeta("property", "og:site_name", "NOOR Design & Build Ltd");
    upsertMeta("property", "og:locale", "en_GB");
    const ogImage = image
      ? image.startsWith("http") ? image : `${SITE}${image}`
      : `${SITE}/og-image.jpg`;
    upsertMeta("property", "og:image", ogImage);
    upsertMeta("property", "og:image:width", String(imageWidth));
    upsertMeta("property", "og:image:height", String(imageHeight));
    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", title);
    upsertMeta("name", "twitter:description", description);
    upsertMeta("name", "twitter:image", ogImage);

    let link = document.head.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement("link");
      link.setAttribute("rel", "canonical");
      document.head.appendChild(link);
    }
    link.setAttribute("href", `${SITE}${path}`);

    const id = "seo-jsonld";
    document.getElementById(id)?.remove();
    if (schema) {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.id = id;
      s.textContent = JSON.stringify(schema);
      document.head.appendChild(s);
    }
  }, [title, description, path, image, imageWidth, imageHeight, schema, noindex]);
  return null;
}

export const localBusinessSchema = (settings, testimonials) => ({
  "@context": "https://schema.org",
  "@type": ["LocalBusiness", "GeneralContractor"],
  name: settings?.company_name || "NOOR Design & Build Ltd",
  legalName: "NOOR Design & Build Ltd",
  identifier: { "@type": "PropertyValue", propertyID: "Companies House Company Number", value: "16409807" },
  address: {
    "@type": "PostalAddress",
    streetAddress: "3rd Floor, Old Stock Exchange, St Nicholas Street",
    addressLocality: "Bristol",
    postalCode: "BS1 1TG",
    addressCountry: "GB",
  },
  description:
    "Premium residential design and build contractor — house extensions, full home renovations and conversions across Bristol, Bath, Somerset and the surrounding areas.",
  url: settings?.site_url || SITE,
  telephone: "+447782760279",
  email: settings?.email || "info@noordb.co.uk",
  logo: `${SITE}/logo-full.png`,
  image: `${SITE}/og-image.jpg`,
  areaServed: ["Bristol", "Bath", "Keynsham", "Weston-super-Mare", "Somerset", "Bridgwater", "Taunton", "Yeovil", "Glastonbury", "Thornbury", "Yatton"],
  slogan: settings?.tagline || "Quality you can see, service you can trust.",
  ...(settings?.google_reviews_url ? { sameAs: [settings.google_reviews_url] } : {}),
  // Genuine client reviews displayed on this site — feeds Google's review rich results
  ...(testimonials?.length
    ? {
        aggregateRating: {
          "@type": "AggregateRating",
          ratingValue: "5.0",
          bestRating: "5",
          reviewCount: String(testimonials.length),
        },
        review: testimonials.map((t) => ({
          "@type": "Review",
          author: { "@type": "Person", name: t.name },
          reviewBody: t.text,
          reviewRating: { "@type": "Rating", ratingValue: "5", bestRating: "5" },
        })),
      }
    : {}),
});

export const breadcrumbSchema = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((it, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: it.name,
    item: `${SITE}${it.path}`,
  })),
});

export const faqSchema = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: (faqs || []).map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
});

export const serviceSchema = (service, settings) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  name: service.title,
  description: service.summary,
  provider: { "@type": "GeneralContractor", name: settings?.company_name || "NOOR Design & Build Ltd" },
  areaServed: ["Bristol", "Bath", "Somerset", "Weston-super-Mare", "Taunton", "Bridgwater", "Yeovil"],
});

export const websiteSchema = (settings) => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: settings?.company_name || "NOOR Design & Build Ltd",
  url: settings?.site_url || SITE,
});
